import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';

import VisionAireLogo from '@/components/VisionAireLogo';

export default function RegisterPage() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const defaultRole = searchParams.get('role') || 'buyer';

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState(defaultRole);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await register(name, email, password, role);
            if (role === 'buyer') navigate('/buyer/intake');
            else if (role === 'seller') navigate('/seller/intake');
            else if (role === 'agent') navigate('/agent');
            else if (role === 'operator') navigate('/operator');
            else navigate('/dashboard');
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (detail) {
                setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
            } else if (err.message === 'Network Error') {
                setError('Unable to connect to server. Please check your connection.');
            } else {
                setError(err.message || 'Something went wrong. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-dark-gradient" data-testid="register-page">
            <Navbar variant="dark" />
            <div className="min-h-screen flex items-center justify-center px-4 pt-16">
                <div className="w-full max-w-md">
                    <div className="card-dark rounded-2xl p-8">
                        <div className="flex items-center justify-center gap-2 mb-8">
                            <VisionAireLogo size={56} />
                        </div>
                        <h1 className="text-2xl font-medium text-white text-center mb-2 font-['Outfit']">Create Your Account</h1>
                        <p className="text-sm text-zinc-400 text-center mb-8">Join VisionAire Realty Services today</p>

                        {error && (
                            <div data-testid="register-error" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                                {error}
                            </div>
                        )}

                        {/* Role Selector */}
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            <button data-testid="role-buyer-btn" type="button"
                                onClick={() => setRole('buyer')}
                                className={`py-3 rounded-lg text-sm font-semibold transition-all ${role === 'buyer' ? 'bg-emerald-700 text-white' : 'bg-white/5 text-zinc-400 border border-white/10 hover:border-white/20'}`}>
                                Buyer
                            </button>
                            <button data-testid="role-seller-btn" type="button"
                                onClick={() => setRole('seller')}
                                className={`py-3 rounded-lg text-sm font-semibold transition-all ${role === 'seller' ? 'bg-amber-600 text-white' : 'bg-white/5 text-zinc-400 border border-white/10 hover:border-white/20'}`}>
                                Seller
                            </button>
                            <button data-testid="role-agent-btn" type="button"
                                onClick={() => setRole('agent')}
                                className={`py-3 rounded-lg text-sm font-semibold transition-all ${role === 'agent' ? 'bg-blue-600 text-white' : 'bg-white/5 text-zinc-400 border border-white/10 hover:border-white/20'}`}>
                                Agent
                            </button>
                            <button data-testid="role-operator-btn" type="button"
                                onClick={() => setRole('operator')}
                                className={`py-3 rounded-lg text-sm font-semibold transition-all ${role === 'operator' ? 'bg-purple-600 text-white' : 'bg-white/5 text-zinc-400 border border-white/10 hover:border-white/20'}`}>
                                Operator
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <Label className="text-zinc-300 text-sm mb-1.5 block">Full Name</Label>
                                <Input data-testid="register-name" type="text" value={name} onChange={e => setName(e.target.value)}
                                    required placeholder="John Smith"
                                    className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-emerald-500" />
                            </div>
                            <div>
                                <Label className="text-zinc-300 text-sm mb-1.5 block">Email</Label>
                                <Input data-testid="register-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                                    required placeholder="you@example.com"
                                    className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-emerald-500" />
                            </div>
                            <div>
                                <Label className="text-zinc-300 text-sm mb-1.5 block">Password</Label>
                                <div className="relative">
                                    <Input data-testid="register-password" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                                        required placeholder="Min 6 characters" minLength={6}
                                        className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-emerald-500 pr-10" />
                                    <button type="button" data-testid="register-toggle-password"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors">
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <Button data-testid="register-submit" type="submit" disabled={loading}
                                className={`w-full rounded-lg py-5 font-semibold ${
                                    role === 'seller' ? 'bg-amber-600 hover:bg-amber-700 text-white' : role === 'agent' ? 'bg-blue-600 hover:bg-blue-700 text-white' : role === 'operator' ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                }`}>
                                {loading ? 'Creating account...' : 'Create Account'} {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
                            </Button>
                        </form>

                        <p className="text-sm text-zinc-400 text-center mt-6">
                            Already have an account?{' '}
                            <Link to="/login" className="text-emerald-400 hover:text-emerald-300 font-medium" data-testid="register-login-link">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
